/**
 * How many things want you, for the header badge.
 *
 * Modules post attention items to Fabric and only the Front Door ever read them, so
 * standing in Vault you had no idea a licence disc expired this morning. The count
 * belongs in every portal's header.
 *
 * Deliberately thin: a count and nothing else. The *items* stay on the Front Door,
 * because a portal showing three of somebody else's notifications is a notification
 * centre, and every module growing one is how six portals end up with six
 * inconsistent inboxes. A number and a link is the whole feature.
 *
 * Fails silent by design. `display` and `guest` roles get 403 on the attention feed —
 * that is a correct answer, not an error — and a Fabric outage must degrade a badge,
 * never the portal you are standing in.
 */
import { getIdToken } from "./auth";

const FABRIC_API = import.meta.env.VITE_FABRIC_API_URL ?? "";

/** Refreshed every five minutes, matching Fabric's own health sweep cadence. */
const EVERY_MS = 5 * 60_000;

class Attention {
  count = $state(0);
  /** Where the badge links to. The Front Door owns the list. */
  href = $state(import.meta.env.VITE_FRONTDOOR_URL ?? "");

  constructor() {
    if (!FABRIC_API) return;
    void this.refresh();
    // Not `setInterval` alone: a laptop that has been asleep for six hours should
    // re-read on waking rather than waiting out the remainder of a timer.
    setInterval(() => void this.refresh(), EVERY_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) void this.refresh();
      });
    }
  }

  async refresh(): Promise<void> {
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await fetch(`${FABRIC_API}/attention`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) return; // 403 for display/guest is the correct answer
      const body = (await res.json()) as { items?: unknown[] };
      this.count = Array.isArray(body.items) ? body.items.length : 0;
    } catch {
      // Leave the previous count rather than zeroing it. Flicking to 0 on a dropped
      // request reads as "nothing needs you", which is a claim this cannot make.
    }
  }
}

export const attention = new Attention();
